import { AlarmClock, CalendarDays, ChevronRight, X } from 'lucide-react-native';
import { useEffect, useState } from 'react';
import {
  Modal,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';

import { useConfetti } from '@/components/ConfettiOverlay';
import { DatePickerSheet } from '@/components/DatePickerSheet';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import { hapticTap } from '@/lib/haptics';

interface RescheduleTask {
  id: string;
  title: string;
}

const PRESETS: { label: string; days: number; sub: string }[] = [
  { label: 'Tomorrow', days: 1, sub: 'Just buying a day' },
  { label: 'In 3 days', days: 3, sub: 'Long-weekend out' },
  { label: 'Next week', days: 7, sub: 'A week out' },
  { label: 'In two weeks', days: 14, sub: "I'll get to it" },
  { label: 'Next month', days: 30, sub: "Off the radar for now" },
  { label: 'Next season', days: 90, sub: 'Quarterly punt' },
];

/**
 * Slide-up reschedule sheet. Replaces the old silent snooze with a
 * deliberate "when next?" choice — preset chips + a custom days field.
 * Mark Done has confetti; Reschedule gets a calmer animated "whoosh"
 * (forest-tinted, no fanfare) so the user still feels something happened.
 */
export function ReschedulePicker({
  task,
  onCancel,
  onPick,
}: {
  task: RescheduleTask | null;
  onCancel: () => void;
  onPick: (days: number) => void;
}) {
  const { fire } = useConfetti();
  const insets = useSafeAreaInsets();
  const [calendarOpen, setCalendarOpen] = useState(false);
  const open = task != null;

  const anim = useSharedValue(0);
  useEffect(() => {
    anim.value = withTiming(open ? 1 : 0, {
      duration: 240,
      easing: Easing.out(Easing.cubic),
    });
  }, [open, anim]);

  useEffect(() => {
    if (!open) setCalendarOpen(false);
  }, [open]);

  const sheetStyle = useAnimatedStyle(() => ({
    opacity: anim.value,
    transform: [
      { translateY: (1 - anim.value) * 60 },
      { scale: 0.96 + anim.value * 0.04 },
    ],
  }));
  const backdropStyle = useAnimatedStyle(() => ({ opacity: anim.value }));

  const pickWithWhoosh = (days: number, e?: { nativeEvent?: { pageX?: number; pageY?: number } }) => {
    // Action first so a flaky touch-event shape (e.g. RN Web's synthetic event
    // not carrying pageX/pageY) can never block the actual reschedule.
    hapticTap();
    onPick(days);
    try {
      const x = e?.nativeEvent?.pageX;
      const y = e?.nativeEvent?.pageY;
      if (typeof x === 'number' && typeof y === 'number') {
        // A small, calm forest burst — not the celebratory honey of "done."
        fire(x, y, 5);
      }
    } catch {
      // Confetti is decoration; never let it break the action.
    }
  };

  // Convert a chosen calendar date (ms) into "days from now" since the
  // task storage layer keeps the relative offset semantics for now.
  const pickDate = (ms: number) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const picked = new Date(ms);
    picked.setHours(0, 0, 0, 0);
    const days = Math.max(1, Math.round((picked.getTime() - today.getTime()) / 86_400_000));
    setCalendarOpen(false);
    onPick(days);
  };

  if (!task) return null;

  return (
    <Modal animationType="none" transparent visible={open} onRequestClose={onCancel}>
      <Animated.View style={[styles.backdrop, backdropStyle]}>
        <Pressable style={styles.backdropPress} onPress={onCancel} />
      </Animated.View>

      <View pointerEvents="box-none" style={styles.wrap}>
        {/* Android 15 edge-to-edge: pad past the gesture nav bar so
            "Open calendar" isn't buried under it (Wave 59). */}
        <Animated.View style={[styles.sheet, { paddingBottom: 16 + insets.bottom }, sheetStyle]}>
          <View style={styles.head}>
            <View style={styles.headLeft}>
              <AlarmClock size={16} color={Palette.forest} strokeWidth={1.8} />
              <Text style={styles.headTitle}>Reschedule</Text>
            </View>
            <Pressable onPress={onCancel} hitSlop={10} style={({ pressed }) => [styles.closeBtn, pressed && { opacity: 0.7 }]}>
              <X size={18} color={Palette.ink} strokeWidth={2} />
            </Pressable>
          </View>

          <Text style={styles.taskLine} numberOfLines={2}>
            {task.title}
          </Text>

          <Text style={styles.sectionLabel}>WHEN SHOULD WE SHOW IT AGAIN?</Text>

          {PRESETS.map((p) => (
            <Pressable
              key={p.label}
              onPress={(e) => pickWithWhoosh(p.days, e as never)}
              style={({ pressed }) => [
                styles.optRow,
                pressed && styles.optRowPressed,
              ]}>
              <View style={styles.optIcon}>
                <CalendarDays size={16} color={Palette.forest} strokeWidth={1.8} />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.optLabel}>{p.label}</Text>
                <Text style={styles.optSub}>{p.sub}</Text>
              </View>
              <ChevronRight size={14} color={Palette.inkFaint} strokeWidth={1.8} />
            </Pressable>
          ))}

          <Text style={[styles.sectionLabel, { marginTop: 14 }]}>OR PICK A SPECIFIC DATE</Text>
          <Pressable
            onPress={() => setCalendarOpen(true)}
            style={({ pressed }) => [
              styles.calendarBtn,
              pressed && { transform: [{ scale: 0.98 }], opacity: 0.92 },
            ]}>
            <CalendarDays size={16} color={Palette.forest} strokeWidth={1.8} />
            <Text style={styles.calendarBtnText}>Open calendar</Text>
            <ChevronRight size={14} color={Palette.inkFaint} strokeWidth={1.8} />
          </Pressable>
        </Animated.View>
      </View>

      <DatePickerSheet
        open={calendarOpen}
        value={null}
        minDate={Date.now()}
        onPick={pickDate}
        onClose={() => setCalendarOpen(false)}
        title="Reschedule to…"
      />
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(15, 24, 18, 0.55)',
  },
  backdropPress: { flex: 1 },
  wrap: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  sheet: {
    width: '100%',
    maxWidth: 440,
    backgroundColor: Palette.sand,
    borderTopLeftRadius: 28,
    borderTopRightRadius: 28,
    paddingHorizontal: 20,
    paddingTop: 16,
    paddingBottom: 28,
    shadowColor: '#000',
    shadowOpacity: 0.18,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: -6 },
    elevation: 20,
  },
  head: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  headLeft: { flexDirection: 'row', alignItems: 'center', gap: 7 },
  headTitle: { fontFamily: Fonts.serifBold, fontSize: 18, color: Palette.ink },
  closeBtn: {
    width: 32, height: 32, borderRadius: 16,
    backgroundColor: Palette.card,
    borderWidth: 1, borderColor: Palette.line,
    alignItems: 'center', justifyContent: 'center',
  },
  taskLine: {
    fontFamily: Fonts.sansSemibold,
    fontSize: 13.5,
    color: Palette.inkSoft,
    marginTop: 4,
    marginBottom: 14,
  },
  sectionLabel: {
    fontFamily: Fonts.sansBold,
    fontSize: 11.5,
    color: Palette.inkFaint,
    letterSpacing: 0.6,
    marginBottom: 6,
  },
  optRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
    borderRadius: Radius.lg,
    paddingHorizontal: 12,
    paddingVertical: 11,
    marginBottom: 6,
  },
  optRowPressed: { backgroundColor: '#EEF3EE', borderColor: Palette.forest },
  optIcon: {
    width: 32, height: 32, borderRadius: 10,
    backgroundColor: '#EAF0EA',
    alignItems: 'center', justifyContent: 'center',
  },
  optLabel: { fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.ink },
  optSub: { fontFamily: Fonts.sansMedium, fontSize: 12.5, color: Palette.inkSoft, marginTop: 1 },
  calendarBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 14,
    paddingVertical: 13,
    borderRadius: Radius.lg,
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
  },
  calendarBtnText: {
    flex: 1,
    fontFamily: Fonts.sansBold,
    fontSize: 14,
    color: Palette.ink,
  },
});

export default ReschedulePicker;
